// =========================================================================
// Matchmaker Quiz — Typeform-style front-end behaviour (BC-52)
//
// Layers a one-question-per-screen UX on top of Formie 3's multi-page ajax
// form (`matchmakerQuiz`). Formie owns page swapping, validation and submit;
// this module only adds:
//   - auto-advance on single-radio pages (~250ms after `change`)
//   - enter-only page reveal animation (CSS keyframe + GSAP child stagger)
//   - focus management + screen-reader announcement on each page change
//
// All motion is gated behind `prefers-reduced-motion: reduce`.
// =========================================================================

import { gsap } from 'gsap'

// -- Tunables --------------------------------------------------------------

/** Delay before auto-advancing, so the selected radio is visibly registered. */
const AUTO_ADVANCE_DELAY = 250

/** GSAP stagger reveal duration/offset for a page's children. */
const REVEAL_DURATION = 0.4
const REVEAL_STAGGER = 0.08

/** Field handles that must NOT auto-advance (multi-select / contact). */
const MULTI_SELECT_HANDLES = ['qualities', 'regions']

// -- Helpers ---------------------------------------------------------------

const prefersReducedMotion = (): boolean =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * The currently visible Formie page within a form.
 * Formie hides inactive pages with the `data-fui-page-hidden` attribute.
 */
const visiblePage = (form: HTMLElement): HTMLElement | null =>
    form.querySelector<HTMLElement>('[data-fui-page]:not([data-fui-page-hidden])')

/**
 * Resolve the page Formie is *about* to show, by id.
 *
 * `onFormiePageToggle` dispatches BEFORE Formie flips the `data-fui-page-hidden`
 * attributes (verified in formie-form-theme.js: dispatch then attribute swap in
 * the same synchronous call). So reading "the visible page" inside the handler
 * still returns the OLD page — we must resolve the target from the event's
 * `nextPageId` instead. Pages carry it on `data-id`.
 */
const pageById = (form: HTMLElement, pageId: unknown): HTMLElement | null => {
    if (pageId === null || pageId === undefined || pageId === '') {
        return null
    }

    return form.querySelector<HTMLElement>(`[data-fui-page][data-id="${pageId}"]`)
}

/**
 * Whether a page should auto-advance: it must contain radio inputs only
 * (single-choice), and none of its fields are in the multi-select/contact
 * exclusion list. The welcome page has no inputs, so it never qualifies.
 */
const isAutoAdvancePage = (page: HTMLElement): boolean => {
    const hasMultiSelect = MULTI_SELECT_HANDLES.some((handle) =>
        page.querySelector(`[data-field-handle="${handle}"]`),
    )

    if (hasMultiSelect) {
        return false
    }

    const radios = page.querySelectorAll<HTMLInputElement>('input[type="radio"]')
    const otherInputs = page.querySelectorAll(
        'input:not([type="radio"]):not([type="hidden"]), select, textarea',
    )

    return radios.length > 0 && otherInputs.length === 0
}

/** The "next page" / submit button rendered inside a Formie page. */
const nextButton = (page: HTMLElement): HTMLButtonElement | null =>
    page.querySelector<HTMLButtonElement>('button[type="submit"], .fui-next')

/**
 * Move focus to the new page's heading (or first focusable input). The
 * focus move is itself the screen-reader announcement: a `tabindex="-1"`
 * heading reads its text on focus, the way an SPA route change announces a
 * new view. No separate live region is needed.
 */
const focusPage = (page: HTMLElement): void => {
    const heading = page.querySelector<HTMLElement>(
        '.fui-heading, legend, .fui-legend, h1, h2, h3',
    )
    const input = page.querySelector<HTMLElement>(
        'input:not([type="hidden"]), select, textarea',
    )
    const target = heading ?? input

    if (!target) {
        return
    }

    if (heading && !heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1')
    }

    target.focus({ preventScroll: true })
}

/**
 * GSAP staggered reveal of a page's direct content rows.
 *
 * Uses `y`/`opacity` (not `autoAlpha`) so targets are never set to
 * `visibility: hidden` — the heading we move focus to must stay focusable and
 * announceable mid-animation.
 */
const revealPage = (page: HTMLElement): void => {
    if (prefersReducedMotion()) {
        return
    }

    const container = page.querySelector<HTMLElement>('[data-quiz-reveal]') ?? page
    const targets = container.querySelectorAll<HTMLElement>(':scope > *')

    if (!targets.length) {
        return
    }

    gsap.fromTo(
        targets,
        { opacity: 0, y: 16 },
        {
            opacity: 1,
            y: 0,
            duration: REVEAL_DURATION,
            stagger: REVEAL_STAGGER,
            ease: 'power2.out',
            clearProps: 'transform,opacity',
        },
    )
}

/**
 * Inject a Typeform-style step index ("1 →") above each question page.
 *
 * Numbers every page that holds an option or input (welcome/intro pages have
 * neither and are skipped). Injected once on init for all pages — in ajax mode
 * every page is already in the DOM. The index goes inside `[data-quiz-reveal]`
 * so it animates in with the rest of the page content.
 */
const numberSteps = (form: HTMLElement): void => {
    const pages = form.querySelectorAll<HTMLElement>('[data-fui-page]')
    let step = 0

    pages.forEach((page) => {
        const isStep = page.querySelector('.fui-radio, .fui-checkbox, .fui-input:not([type="hidden"])')

        if (!isStep) {
            return
        }

        step += 1

        if (page.querySelector('.quiz-step-index')) {
            return
        }

        const container = page.querySelector<HTMLElement>('[data-quiz-reveal]') ?? page
        const index = document.createElement('div')
        index.className = 'quiz-step-index'
        index.setAttribute('aria-hidden', 'true')
        index.textContent = `${step} →`
        container.prepend(index)
    })
}

/**
 * Add directional arrow icons to the nav buttons, mirroring the button atoms
 * (button--primary / button--secondary use a trailing FA arrow). Next/submit
 * get a right arrow, previous a left one. FontAwesome's `dom.watch` (loaded via
 * app.ts) converts the injected `<i>` tags to SVG. Idempotent.
 */
const decorateButtons = (form: HTMLElement): void => {
    const addArrow = (btn: HTMLElement, icon: string, position: 'start' | 'end'): void => {
        if (btn.querySelector('.fui-arrow')) {
            return
        }

        const arrow = document.createElement('i')
        arrow.className = `fui-arrow fa-solid ${icon}`
        arrow.setAttribute('aria-hidden', 'true')
        position === 'start' ? btn.prepend(arrow) : btn.append(arrow)
    }

    form.querySelectorAll<HTMLElement>('.fui-next, .fui-submit').forEach((btn) =>
        addArrow(btn, 'fa-arrow-right', 'end'),
    )
    form.querySelectorAll<HTMLElement>('.fui-prev').forEach((btn) =>
        addArrow(btn, 'fa-arrow-left', 'start'),
    )
}

// -- Wiring ----------------------------------------------------------------

const initForm = (form: HTMLElement): void => {
    // Auto-advance on radio change for single-choice pages.
    form.addEventListener('change', (event) => {
        const input = event.target as HTMLInputElement

        if (!input || input.type !== 'radio') {
            return
        }

        const page = input.closest<HTMLElement>('[data-fui-page]')

        if (!page || !isAutoAdvancePage(page)) {
            return
        }

        const button = nextButton(page)

        if (!button) {
            return
        }

        window.setTimeout(() => button.click(), AUTO_ADVANCE_DELAY)
    })

    // Enter-only reveal + focus/announce on every page toggle. Formie fires
    // `onFormiePageToggle` on the <form> just *before* it swaps the
    // `data-fui-page-hidden` attributes, so resolve the incoming page from the
    // event payload (`nextPageId`) and defer DOM work to the next frame, once
    // Formie has unhidden it.
    form.addEventListener('onFormiePageToggle', (event) => {
        const detail = (event as CustomEvent).detail
        const nextPageId = detail?.data?.nextPageId

        window.requestAnimationFrame(() => {
            const page = pageById(form, nextPageId) ?? visiblePage(form)

            if (!page) {
                return
            }

            focusPage(page)
            revealPage(page)
        })
    })

    // Keyboard select: A/B/C… picks the matching option on the visible page.
    // Bound at document level so the fullscreen quiz captures keys anywhere on
    // the takeover screen. When embedded inline (`.quiz--inline`), the quiz is
    // one element among page content, so only act while focus is inside it —
    // otherwise typing A/B/C while reading the page would hijack the quiz.
    const isInline = form.classList.contains('quiz--inline')

    document.addEventListener('keydown', (event) => {
        if (event.metaKey || event.ctrlKey || event.altKey) {
            return
        }

        if (isInline && !form.contains(document.activeElement)) {
            return
        }

        const key = event.key.toUpperCase()

        if (!/^[A-Z]$/.test(key)) {
            return
        }

        const target = event.target as HTMLElement | null
        const tag = target?.tagName?.toLowerCase()
        const type = (target as HTMLInputElement | null)?.type

        // Don't hijack typing in text inputs / textareas / selects.
        if (tag === 'textarea' || tag === 'select') {
            return
        }

        if (tag === 'input' && type !== 'radio' && type !== 'checkbox') {
            return
        }

        const page = visiblePage(form)

        if (!page) {
            return
        }

        const labels = page.querySelectorAll<HTMLElement>('.fui-radio-label, .fui-checkbox-label')
        const index = key.charCodeAt(0) - 65

        if (index >= 0 && index < labels.length) {
            event.preventDefault()
            labels[index].click()
        }
    })

    // Inject Typeform-style step indices ("1 →") above each question.
    numberSteps(form)

    // Add atom-style directional arrows to the nav buttons.
    decorateButtons(form)

    // Reveal the first (welcome) page on initial init.
    const first = visiblePage(form)

    if (first) {
        revealPage(first)
    }
}

const main = (): void => {
    const forms = document.querySelectorAll<HTMLElement>('[data-quiz-form]')
    forms.forEach((form) => initForm(form))
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', main)
} else {
    main()
}

// Accept HMR -- https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log('HMR active')
    })
}
